<template>
  <div class="container-fluid bg-white container-fluid rounded-4 contenedor-principal card shadow-sm">
    <DxAccordion :collapsible="true" :multiple="false" :animation-duration="200" width="100%" class="buscador-ofertas-filtros-accordion"
      item-title-template="tituloFiltros">
      <template #tituloFiltros>
        <h1 class="p-2 mb-0">{{ $t("mis_datos.comisiones.filtros.titulo") }}</h1>
      </template>
      <DxItem>
        <div class="buscador-ofertas-filtros px-2 py-4 pt-0">
          <div class="filtros-grid">
            <div class="filtro-field">
              <label class="dx-field-item-label-text" for="filtro-comercializadora">
                {{ $t('mis_datos.comisiones.filtros.comercializadora') }}
              </label>
              <DxSelectBox id="filtro-comercializadora" v-model="filtros.Comercializadora" :data-source="comercializadoras" value-expr="Id"
                display-expr="Denominacion" :search-enabled="true" :show-clear-button="true">
                <DxValidator ref="dxValidatorRef">
                  <DxRequiredRule :message="$t('mis_datos.comisiones.filtros.comercializadoraRequerida')" />
                </DxValidator>
              </DxSelectBox>
            </div>

            <div class="filtro-field filtro-field--small">
              <label class="dx-field-item-label-text" for="filtro-tipo-energia">
                {{ $t("mis_datos.comisiones.filtros.tipoEnergia") }}
              </label>
              <DxSelectBox id="filtro-tipo-energia" v-model="filtros.TipoEnergia" v-bind="getSelectBoxFromApiEnum(TipoSuministroTramiteEnergiaEnum)" />
            </div>

            <div class="filtro-field filtro-field--small">
              <label class="dx-field-item-label-text" for="filtro-persona">
                {{ $t("mis_datos.comisiones.filtros.persona") }}
              </label>
              <DxSelectBox id="filtro-persona" v-model="filtros.TipoPersona" v-bind="getSelectBoxFromApiEnum(TipoPersonaEnum)" />
            </div>

            <div class="filtro-field filtro-field--checkbox">
              <label class="dx-field-item-label-text" for="filtro-autoconsumo">
                {{ $t("mis_datos.comisiones.filtros.autoconsumo-label") }}
              </label>
              <DxCheckBox id="filtro-autoconsumo" v-model="filtros.ConAutoconsumo" :text="$t('buscador-ofertas.filtros.autoconsumo')" :disabled="esFiltroGas" />
            </div>

            <div class="filtro-field filtro-field--potencia">
              <label class="dx-field-item-label-text" for="filtro-potencia">
                {{ $t("mis_datos.comisiones.filtros.potencia") }} (kW)
              </label>
              <div class="potencia-range-row">
                <DxNumberBox v-model="filtros.PotenciaMin" :min="potenciaMinLimit" :max="potenciaMaxLimit" class="potencia-number-box"
                  :disabled="esFiltroGas" />
                <DxRangeSlider id="filtro-potencia" ref="rangeSliderRef" v-model="potenciaRange" :min="potenciaMinLimit" :max="potenciaMaxLimit" :step="0.1"
                  :show-range="true" class="potencia-slider" :disabled="esFiltroGas" />
                <DxNumberBox v-model="filtros.PotenciaMax" :min="potenciaMinLimit" :max="potenciaMaxLimit" class="potencia-number-box"
                  :disabled="esFiltroGas" />
              </div>
            </div>

            <div class="filtro-field filtro-field--actions">
              <label class="dx-field-item-label-text" aria-hidden="true">&nbsp;</label>
              <div class="acciones-filtros">
                <DxButton v-bind="{ ...primaryButtonProps(), icon: 'fa-regular fa-filter fa-sm' }" :text="$t('mis_datos.comisiones.filtros.btnFiltrar')"
                  @click="aplicarFiltros" />
                <DxButton v-bind="{ ...textButtonProps() }" :text="$t('mis_datos.comisiones.filtros.btnLimpiar')" @click="limpiarFiltros" />
              </div>
            </div>
          </div>
        </div>
      </DxItem>
    </DxAccordion>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref, nextTick, onBeforeMount } from "vue";
import { DxButton, DxCheckBox, DxNumberBox, DxSelectBox } from "devextreme-vue";
import DxRangeSlider from "devextreme-vue/range-slider";
import DxAccordion, { DxItem } from "devextreme-vue/accordion";
import { getSelectBoxFromApiEnum } from "@helpers/enums-helper";
import {
  TipoSuministroTramiteEnergiaEnum,
  TipoPersonaEnum,
  type SelectBoxGuidEntityModel,
  TipoEmpresaServiciosEnum,
  type OfertasEnergiaRetribucionesListFiltrosModel,
} from "@api/api";
import { primaryButtonProps, textButtonProps } from "@/.shared/helpers/button-helper";
import { useApi } from "@/.shared/.api/integration";
import { useIntl } from "@/.shared/services/i18n";
import DxValidator, { DxRequiredRule } from "devextreme-vue/cjs/validator";
import { toastError } from "@/.shared/services/notification-service";

const POTENCIA_MIN_DEFAULT = 0;
const POTENCIA_MAX_DEFAULT = 1000;
const potenciaMinLimit = 0;
const potenciaMaxLimit = 1000;

const filtros = reactive<OfertasEnergiaRetribucionesListFiltrosModel>({
  Comercializadora: "",
  TipoEnergia: null,
  TipoPersona: null,
  ConAutoconsumo: false,
  PotenciaMin: POTENCIA_MIN_DEFAULT,
  PotenciaMax: POTENCIA_MAX_DEFAULT,
});

const rangeSliderRef = ref<InstanceType<typeof DxRangeSlider> | null>(null);
const dxValidatorRef = ref<InstanceType<typeof DxValidator> | null>(null);
const comercializadoras = ref<SelectBoxGuidEntityModel[]>([]);
const api = useApi();
const intl = useIntl();

const esFiltroGas = computed(
  () => filtros.TipoEnergia === TipoSuministroTramiteEnergiaEnum.Gas,
);

onBeforeMount(() => {
  getComercializadorasDatasource();
});

async function getComercializadorasDatasource(): Promise<void> {
  const data = await api.callStandardApi("getEmpresasServiciosOperablesSelector", { showLoading: false }, undefined, [TipoEmpresaServiciosEnum.Energia]);
  comercializadoras.value = data.orderBy((c) => c.Denominacion).toArray();
}

watch(
  () => filtros.TipoEnergia,
  (tipo) => {
    if (tipo === TipoSuministroTramiteEnergiaEnum.Gas) {
      filtros.ConAutoconsumo = null;
      filtros.PotenciaMin = null;
      filtros.PotenciaMax = null;
    } else {
      if (filtros.ConAutoconsumo == null) {
        filtros.ConAutoconsumo = false;
      }
      if (filtros.PotenciaMin == null) {
        filtros.PotenciaMin = POTENCIA_MIN_DEFAULT;
      }
      if (filtros.PotenciaMax == null) {
        filtros.PotenciaMax = POTENCIA_MAX_DEFAULT;
      }
    }
  },
);

const potenciaRange = computed({
  get: () => [
    filtros.PotenciaMin ?? POTENCIA_MIN_DEFAULT,
    filtros.PotenciaMax ?? POTENCIA_MAX_DEFAULT,
  ],
  set: (val: number[]) => {
    if (esFiltroGas.value) {
      return;
    }
    filtros.PotenciaMin = val[0] ?? POTENCIA_MIN_DEFAULT;
    filtros.PotenciaMax = val[1] ?? POTENCIA_MAX_DEFAULT;
  },
});

watch(
  () => [filtros.PotenciaMin, filtros.PotenciaMax],
  async () => {
    if (filtros.PotenciaMin != null && filtros.PotenciaMax != null && filtros.PotenciaMin > filtros.PotenciaMax) {
      filtros.PotenciaMax = filtros.PotenciaMin;
    }
    await nextTick();
    if (rangeSliderRef.value?.instance) {
      rangeSliderRef.value.instance.option("value", [
        filtros.PotenciaMin ?? POTENCIA_MIN_DEFAULT,
        filtros.PotenciaMax ?? POTENCIA_MAX_DEFAULT,
      ]);
    }
  },
);

const emit = defineEmits<{
  filtrar: [filtros: OfertasEnergiaRetribucionesListFiltrosModel];
}>();

function aplicarFiltros(): void {
  const validationResult = dxValidatorRef.value?.instance.validate();
  if (!validationResult?.isValid) {
    toastError(intl.i18n.t("mis_datos.comisiones.filtros.comercializadoraRequerida"));
    return;
  }
  const payload = { ...filtros };
  emit("filtrar", payload);
}

function limpiarFiltros(): void {
  filtros.Comercializadora = "";
  filtros.TipoEnergia = null;
  filtros.TipoPersona = null;
  filtros.ConAutoconsumo = false;
  filtros.PotenciaMin = POTENCIA_MIN_DEFAULT;
  filtros.PotenciaMax = POTENCIA_MAX_DEFAULT;
  emit("filtrar", { ...filtros });
}

defineExpose({
  aplicarFiltros,
});
</script>

<style lang="scss" scoped>
.buscador-ofertas-filtros {
  .filtros-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: flex-end;
  }

  .filtro-field {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1 1 14rem;
  }

  .filtro-field--small {
    flex: 1 1 10rem;
  }

  .filtro-field--checkbox {
    flex: 0 1 11rem;

    :deep(.dx-checkbox) {
      height: 36px;
      display: inline-flex;
      align-items: center;
    }
  }

  .filtro-field--potencia {
    flex: 1 1 18rem;
    max-width: 28rem;
  }

  .filtro-field--actions {
    flex: 0 1 auto;
    margin-inline-start: auto;
  }

  .acciones-filtros {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .potencia-range-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  .potencia-number-box {
    flex: 0 0 4rem;
    width: 4rem;
    min-width: 4rem;
  }

  .potencia-slider {
    flex: 1 1 auto;
    min-width: 0;
  }

  @media (max-width: 576px) {
    .filtro-field,
    .filtro-field--small,
    .filtro-field--checkbox,
    .filtro-field--potencia,
    .filtro-field--actions {
      flex: 1 1 100%;
    }

    .filtro-field--actions {
      margin-inline-start: 0;
    }

    .potencia-range-row {
      flex-wrap: wrap;
    }

    .potencia-slider {
      flex-basis: 100%;
      order: 3;
    }

    .acciones-filtros {
      flex-direction: column;
      align-items: stretch;
    }
  }
}


/*
 * Ajustes en el accordion de servicios y descuentos.
 * Ordenamos caption primero y el icono después para alinear la flecha a la derecha.
 */
:deep(.buscador-ofertas-filtros-accordion.dx-accordion) {
  border: none;
  background: transparent;
}

:deep(.buscador-ofertas-filtros-accordion .dx-accordion-item-title-caption) {
  order: 1;
  margin-inline-end: 0;
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.buscador-ofertas-filtros-accordion .dx-accordion-item-title::before) {
  order: 2;
  margin-inline-end: 0;
  margin-inline-start: auto;
}

:deep(.buscador-ofertas-filtros-accordion .dx-accordion-item > .dx-accordion-item-title) {
  background: var(--background-surface-default) !important;
  color: var(--text-primary-default) !important;
}
</style>
